import { type QueryClient } from '@tanstack/react-query';
import { type clusterService } from '@/api/services/cluster';
import { type quotaService } from '@/api/services/quota';
import { asyncResolver } from '@/router/asyncResolver';

interface IResolveMaxClusterQuotaParams {
  queryClient: QueryClient;
  getAccountQuotasQuery: ReturnType<typeof quotaService.getAccountQuotasQuery>;
}

async function resolveClusterCount({
  queryClient,
  clustersByAccountIdQuery,
  getAccountQuotasQuery,
}: IResolveMaxClusterQuotaParams & {
  clustersByAccountIdQuery: ReturnType<typeof clusterService.listClustersQuery>;
}) {
  const clustersResponse = await queryClient.fetchQuery(clustersByAccountIdQuery);
  const clusters = clustersResponse.items ?? [];

  return { clusterCount: clusters.length, queryClient, getAccountQuotasQuery };
}

const resolveMaxClusterQuota = async ({
  clusterCount,
  queryClient,
  getAccountQuotasQuery,
}: IResolveMaxClusterQuotaParams & {
  clusterCount: number;
}) => {
  const { maxClusters } = await queryClient.fetchQuery(getAccountQuotasQuery);

  return { clusterCount, maxClusters };
};

/**
 * Resolver to check if the number of clusters is within the allowed quota.
 * It fetches the current number of clusters and compares it with the maximum allowed quota.
 */
export const maxClusterQuotaResolver = asyncResolver(
  resolveClusterCount,
  resolveMaxClusterQuota,
  ({ clusterCount, maxClusters }) => clusterCount < maxClusters,
);
